import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Loader2, Search, CheckCircle, AlertCircle, Brain, Settings } from 'lucide-react';
import { useHybridExtraction } from '@/hooks/useHybridExtraction';
import { EnhancedExtractionProgress } from '@/components/EnhancedExtractionProgress';
import { ExtractionErrorRecovery } from '@/components/ExtractionErrorRecovery';
import { ExtractionOptimizationPanel } from '@/components/ExtractionOptimizationPanel';

interface AIFieldSearchPanelProps {
  extractedText: string;
  onFieldUpdate?: (fieldName: string, value: any) => void;
  showOptimization?: boolean;
}

const FIELD_LABELS = {
  customer_name: 'Customer Name',
  account_number: 'Account Number',
  bill_date: 'Bill Date',
  amount: 'Total Amount',
  due_date: 'Due Date',
  supplier_name: 'Supplier Name',
  service_type: 'Service Type',
  billing_period_start: 'Billing Period Start',
  billing_period_end: 'Billing Period End',
  usage_amount: 'Usage Amount',
  usage_unit: 'Usage Unit',
  bill_number: 'Bill Number'
};

export function AIFieldSearchPanel({ extractedText, onFieldUpdate, showOptimization = false }: AIFieldSearchPanelProps) {
  const { 
    session, 
    isSearching, 
    searchResults, 
    searchField, 
    getFieldConfidence, 
    getFieldValue, 
    isMissingField,
    updateFieldValue,
    getExtractionProgress 
  } = useHybridExtraction();
  
  const [searchingField, setSearchingField] = useState<string | null>(null);
  const [manualValues, setManualValues] = useState<Record<string, string>>({});
  const [extractionError, setExtractionError] = useState<any>(null);
  const [showOptimizationPanel, setShowOptimizationPanel] = useState(false);

  const handleSearchField = async (fieldName: string) => {
    setSearchingField(fieldName);
    
    const context = session ? {
      extractedData: session.extractedData,
      confidenceScores: session.confidenceScores
    } : {};
    
    const previousAttempts = searchResults[fieldName]?.suggestions || [];
    
    await searchField(fieldName, extractedText, { context, previousAttempts });
    setSearchingField(null);
  };

  const handleManualUpdate = (fieldName: string) => {
    const value = manualValues[fieldName];
    if (value) {
      updateFieldValue(fieldName, value);
      onFieldUpdate?.(fieldName, value);
      setManualValues(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const getFieldStatus = (fieldName: string) => {
    const confidence = getFieldConfidence(fieldName);
    const value = getFieldValue(fieldName);
    const isMissing = isMissingField(fieldName);
    
    if (value && confidence > 0.8) return 'high';
    if (value && confidence > 0.5) return 'medium';
    if (isMissing) return 'missing';
    return 'low';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'high': return 'bg-success text-success-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'missing': return 'bg-destructive text-destructive-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'high': return <CheckCircle className="h-4 w-4" />;
      case 'medium': return <AlertCircle className="h-4 w-4" />;
      case 'missing': return <Search className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (!session) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Field Search
          </CardTitle>
          <CardDescription>
            Start an extraction session to search for missing fields
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const missingFields = session.missingFields || [];
  const extractionProgress = getExtractionProgress();

  // Enhanced metrics for progress component
  const extractionMetrics = {
    totalFields: Object.keys(session.extractedData).length + session.missingFields.length,
    extractedFields: Object.keys(session.extractedData).length,
    highConfidenceFields: Object.values(session.confidenceScores).filter(c => c > 0.8).length,
    lowConfidenceFields: Object.values(session.confidenceScores).filter(c => c > 0.5 && c <= 0.8).length,
    missingFields: session.missingFields.length,
    processingTimeMs: session.processingTimeMs || 0,
    aiProvider: session.aiProvider || 'OpenAI',
    confidenceScore: Object.values(session.confidenceScores).length > 0 
      ? Object.values(session.confidenceScores).reduce((a, b) => a + b, 0) / Object.values(session.confidenceScores).length
      : 0
  };

  const handleRetryExtraction = async () => {
    setExtractionError(null);
    // Implementation would call extraction retry
  };

  const handleManualFallback = () => {
    setExtractionError(null);
    // Switch to manual mode
  };

  const handleReportIssue = async (feedback: string) => {
    // Report issue implementation
    console.log('Reported issue:', feedback);
  };

  return (
    <div className="space-y-4">
      {/* Enhanced Progress Display */}
      <EnhancedExtractionProgress
        metrics={extractionMetrics}
        isProcessing={isSearching}
        onRetryExtraction={handleRetryExtraction}
        onViewDetails={() => setShowOptimizationPanel(true)}
      />

      {/* Error Recovery */}
      {extractionError && (
        <ExtractionErrorRecovery
          error={extractionError}
          onRetry={handleRetryExtraction}
          onManualFallback={handleManualFallback}
          onReportIssue={handleReportIssue}
          isRetrying={isSearching}
        />
      )}

      {/* Optimization Panel */}
      {showOptimization && showOptimizationPanel && (
        <ExtractionOptimizationPanel />
      )}

    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          AI Field Search
          {showOptimization && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOptimizationPanel(!showOptimizationPanel)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          Use AI to find missing or low-confidence fields
        </CardDescription>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Extraction Progress</span>
            <span>{extractionProgress}%</span>
          </div>
          <Progress value={extractionProgress} className="h-2" />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {missingFields.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Missing Fields</h4>
            {missingFields.map(fieldName => (
              <div key={fieldName} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={fieldName}>
                    {FIELD_LABELS[fieldName as keyof typeof FIELD_LABELS] || fieldName}
                  </Label>
                  <Badge className={getStatusColor('missing')}>
                    {getStatusIcon('missing')}
                    Missing
                  </Badge>
                </div>
                
                <div className="flex gap-2">
                  <Input
                    id={fieldName}
                    placeholder="Enter value manually or use AI search"
                    value={manualValues[fieldName] || ''}
                    onChange={(e) => setManualValues(prev => ({ 
                      ...prev, 
                      [fieldName]: e.target.value 
                    }))}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearchField(fieldName)}
                    disabled={isSearching}
                  >
                    {searchingField === fieldName ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleManualUpdate(fieldName)}
                    disabled={!manualValues[fieldName]}
                  >
                    Set
                  </Button>
                </div>
                
                {searchResults[fieldName] && (
                  <div className="p-3 bg-muted rounded-md text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">AI Search Result:</span>
                      <Badge variant="outline">
                        {Math.round(searchResults[fieldName].confidence * 100)}% confidence
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">
                      {searchResults[fieldName].reasoning}
                    </p>
                    {searchResults[fieldName].suggestions.length > 0 && (
                      <div>
                        <span className="font-medium">Suggestions:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {searchResults[fieldName].suggestions.map((suggestion, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {suggestion}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {Object.keys(session.extractedData).length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Extracted Fields</h4>
            {Object.entries(session.extractedData).map(([fieldName, value]) => {
              const status = getFieldStatus(fieldName);
              const confidence = getFieldConfidence(fieldName);
              
              return (
                <div key={fieldName} className="flex items-center justify-between p-2 bg-muted rounded-md">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {FIELD_LABELS[fieldName as keyof typeof FIELD_LABELS] || fieldName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {String(value)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {Math.round(confidence * 100)}%
                    </Badge>
                    <Badge className={getStatusColor(status)}>
                      {getStatusIcon(status)}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {missingFields.length === 0 && Object.keys(session.extractedData).length > 0 && (
          <div className="text-center py-4">
            <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              All fields have been extracted successfully!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}